import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
const ITEMS_PER_PAGE = 7;

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}

function getToday() {
  return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M3 7C3 5.9 3.9 5 5 5H10L12 7H19C20.1 7 21 7.9 21 9V18C21 19.1 20.1 20 19 20H5C3.9 20 3 19.1 3 18V7Z" fill="#F59E0B" />
  </svg>
);

const PdfIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#EF4444" />
    <path d="M14 2V8H20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <text x="7" y="17" fontSize="5" fill="white" fontWeight="bold" fontFamily="sans-serif">PDF</text>
  </svg>
);

const ImageFileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" fill="#3B82F6" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="white" />
    <path d="M5 17L8 13L11 15L14 11L19 17H5Z" fill="white" />
  </svg>
);

const GenericIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#6B7280" />
    <path d="M14 2V8H20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SignedBadge = () => (
  <span style={{ marginLeft: "8px", padding: "2px 8px", fontSize: "11px", fontWeight: 600, borderRadius: "999px", background: "#DCFCE7", color: "#15803D" }}>
    Signed
  </span>
);

const VerifyBadge = ({ status }) => {
  if (status !== "verified" && status !== "failed") return null;
  const ok = status === "verified";
  return (
    <span style={{ marginLeft: "8px", padding: "2px 8px", fontSize: "11px", fontWeight: 600, borderRadius: "999px", background: ok ? "#D1FAE5" : "#FEE2E2", color: ok ? "#047857" : "#B91C1C" }}>
      {ok ? "Verified" : "Verification failed"}
    </span>
  );
};

const FileIcon = ({ type }) => {
  if (type === "folder") return <FolderIcon />;
  if (type === "pdf") return <PdfIcon />;
  if (type === "image") return <ImageFileIcon />;
  return <GenericIcon />;
};

export default function Drive() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [shareTarget, setShareTarget] = useState("");
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef();
  const nextId = useRef(1);
  const [deleting, setDeleting] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(String(value).replace(" ", "T"));
    if (isNaN(d.getTime())) return "-";
    return (
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const loadFiles = async () => {
    try {
      const ownerEmail = localStorage.getItem("email");
      const response = await fetch(
  `${API_URL}/api/secure-drive/files?owner_email=${ownerEmail}`
);
      const data = await response.json();
      const formattedFiles = data.map((file) => ({
        id: file.id,
        file_name: file.file_name,
        name: file.file_name,
        cid: file.cid,
        file_size: file.file_size,
        file_type: file.file_type,
        owner_email: file.owner_email,
        encrypt: file.encrypt,
        pin: file.pin,
        redundancy: file.redundancy,
        source: file.source,
        uploaded_at: file.uploaded_at,
        updated_at: file.updated_at,
        verification_status: file.verification_status,
        verified_at: file.verified_at,
        type: file.file_name?.endsWith(".pdf") ? "pdf" : "file",
        owner: "me",
        size: formatBytes(file.file_size || 0),
        version: file.version || 1,
        // "Modified" = last time a version was saved (e.g. after signing), else the upload time
        modified: formatDateTime(file.updated_at || file.uploaded_at),
        download_url: `${API_URL}/api/secure-drive/download/${file.id}?owner_email=${encodeURIComponent(ownerEmail || "")}`
      }));
      setFiles(formattedFiles);
    } catch (error) {
      console.error("Error loading files:", error);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    try {
      const formData = new FormData();
      formData.append("file", selectedFiles[0]);
      formData.append("owner_email", localStorage.getItem("email"));
      const response = await fetch(
  `${API_URL}/api/secure-drive/upload`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        showToast("File uploaded successfully");
        loadFiles();
      }
    } catch (err) {
      console.error(err);
      showToast("Upload failed");
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const filtered = files.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const pageIds = paginated.map(f => f.id);
    const allSelected = pageIds.every(id => selected.includes(id));
    setSelected(allSelected ? selected.filter(id => !pageIds.includes(id)) : [...new Set([...selected, ...pageIds])]);
  };

  const handleNewFolder = () => {
    if (!newFolderName.trim()) return;
    setFiles(prev => [{
      id: `folder-${nextId.current++}`,
      type: "folder",
      name: newFolderName.trim(),
      owner: "me",
      cid: "bafybei" + Math.random().toString(36).slice(2, 10) + "...",
      size: "—",
      version: 1,
      modified: getToday(),
    }, ...prev]);
    setNewFolderName("");
    setModal(null);
    showToast("Folder created");
  };

  const handleShare = () => {
    if (!shareTarget.trim()) return;
    showToast(`Shared ${selected.length > 0 ? selected.length + " item(s)" : "link"} with ${shareTarget}`);
    setShareTarget("");
    setModal(null);
  };

  // Step 1: ask for confirmation (deletion is permanent)
  const requestDelete = () => {
    if (!selected.length) { showToast("Select items to delete"); setModal(null); return; }
    setModal("confirmDelete");
  };

  // Step 2: permanently delete on the server, then remove from the UI
  const handleDelete = async () => {
    const ownerEmail = localStorage.getItem("email") || "";
    const toDelete = files.filter(f => selected.includes(f.id));
    const deletedIds = [];
    const failed = [];

    setDeleting(true);
    for (const f of toDelete) {
      // folders only exist in the UI (no backend record yet)
      if (f.type === "folder") { deletedIds.push(f.id); continue; }
      try {
        const res = await fetch(
          `${API_URL}/api/secure-drive/delete/${f.id}?owner_email=${encodeURIComponent(ownerEmail)}`,
          { method: "DELETE" }
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success !== false) deletedIds.push(f.id);
        else failed.push(f.name);
      } catch (err) {
        console.error("Delete failed", err);
        failed.push(f.name);
      }
    }
    setDeleting(false);

    // only remove what the server actually deleted
    setFiles(prev => prev.filter(f => !deletedIds.includes(f.id)));
    setSelected(prev => prev.filter(id => !deletedIds.includes(id)));
    setModal(null);

    if (failed.length) showToast(`Could not delete: ${failed.join(", ")}`);
    else showToast(`${deletedIds.length} item(s) permanently deleted`);
  };

  const handleDownload = () => {
    if (!selected.length) return;
    const file = files.find(f => f.id === selected[0]);
    if (file?.download_url) {
      window.open(file.download_url, "_blank");
    }
  };

  // ─── Shared style tokens ───────────────────────────────────────────────────
  const FONT = "'Inter', 'Segoe UI', sans-serif";

  const btnBase = {
    display: "flex", alignItems: "center", gap: "6px",
    borderRadius: "8px", padding: "7px 14px", cursor: "pointer",
    fontSize: "14px", fontWeight: 500, border: "1px solid #E2E8F0",
    background: "#FFFFFF", color: "#374151", fontFamily: FONT,
  };

  const modalBox = {
    background: "#FFFFFF", borderRadius: "16px", padding: "28px 28px 22px",
    minWidth: "320px", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", zIndex: 101,
    border: "1px solid #E2E8F0", fontFamily: FONT,
  };

  return (
    <div style={{ fontFamily: FONT, background: "#ffffff", minHeight: "100vh", color: "#1A1D23", padding: "0px" }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "28px", left: "50%", transform: "translateX(-50%)",
          background: "#1F2937", color: "#fff", padding: "10px 22px", borderRadius: "8px",
          fontSize: "14px", fontWeight: 500, zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          fontFamily: FONT,
        }}>{toast}</div>
      )}

      {/* ── Modals ── */}
      {modal && (
        <div onClick={() => setModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={modalBox}>
            {modal === "newFolder" && (
              <>
                <h3 style={{ margin: "0 0 16px", fontSize: "16px", fontWeight: 600, fontFamily: FONT }}>New Folder</h3>
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleNewFolder()} placeholder="Folder name"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", fontFamily: FONT }} />
                <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
                  <button onClick={() => setModal(null)} style={btnBase}>Cancel</button>
                  <button onClick={handleNewFolder} style={{ ...btnBase, background: "#2563EB", color: "#fff", border: "none" }}>Create</button>
                </div>
              </>
            )}
            {modal === "share" && (
              <>
                <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 600, fontFamily: FONT }}>Share</h3>
                <p style={{ fontSize: "14px", color: "#6B7280", margin: "0 0 14px", fontFamily: FONT }}>
                  {selected.length > 0 ? `Sharing ${selected.length} selected item(s)` : "Share a link to your drive"}
                </p>
                <input autoFocus value={shareTarget} onChange={e => setShareTarget(e.target.value)} onKeyDown={e => e.key === "Enter" && handleShare()} placeholder="Email or name"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", fontFamily: FONT }} />
                <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
                  <button onClick={() => setModal(null)} style={btnBase}>Cancel</button>
                  <button onClick={handleShare} style={{ ...btnBase, background: "#2563EB", color: "#fff", border: "none" }}>Share</button>
                </div>
              </>
            )}
            {modal === "confirmDelete" && (
              <>
                <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 600, fontFamily: FONT, color: "#DC2626" }}>Delete permanently?</h3>
                <p style={{ fontSize: "14px", color: "#6B7280", margin: "0 0 4px", fontFamily: FONT }}>
                  {selected.length} item(s) will be permanently deleted from Secure Vault and unpinned from IPFS.
                </p>
                <p style={{ fontSize: "14px", color: "#6B7280", margin: 0, fontFamily: FONT }}>This action cannot be undone.</p>
                <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
                  <button onClick={() => setModal(null)} disabled={deleting} style={btnBase}>Cancel</button>
                  <button onClick={handleDelete} disabled={deleting} style={{ ...btnBase, background: "#DC2626", color: "#fff", border: "none", opacity: deleting ? 0.6 : 1 }}>
                    {deleting ? "Deleting..." : "Delete Permanently"}
                  </button>
                </div>
              </>
            )}
            {modal === "more" && (
              <>
                <h3 style={{ margin: "0 0 14px", fontSize: "16px", fontWeight: 600, fontFamily: FONT }}>More Actions</h3>
                {[
                  { label: "Download selected", action: handleDownload, danger: false },
                  { label: "Delete selected", action: requestDelete, danger: true },
                ].map(item => (
                  <button key={item.label} onClick={item.action} style={{
                    display: "block", width: "100%", textAlign: "left", padding: "10px 14px", marginBottom: "6px",
                    background: item.danger ? "#FEF2F2" : "#F9FAFB",
                    color: item.danger ? "#EF4444" : "#111827",
                    border: "1px solid " + (item.danger ? "#FECACA" : "#E2E8F0"),
                    borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500, fontFamily: FONT,
                  }}>{item.label}</button>
                ))}
                <div style={{ textAlign: "right", marginTop: "8px" }}>
                  <button onClick={() => setModal(null)} style={btnBase}>Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "24px" }}>
        {[
          { label: "Total Storage", value: "0 GB", sub: "of 500 GB used", color: "#2563EB" },
          { label: "Files", value: files.filter(f => f.type !== "folder").length.toString(), sub: "Total files", color: "#10B981" },
          { label: "Folders", value: files.filter(f => f.type === "folder").length.toString(), sub: "Total folders", color: "#F59E0B" },
          { label: "CID Objects (PPS)", value: files.length.toString(), sub: "Total pinned", color: "#6366F1" },
        ].map((card) => (
          <div key={card.label} style={{
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            padding: "24px",
          }}>
            <p style={{ fontSize: "11px", color: "#9CA3AF", marginBottom: "6px", marginTop: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 500, fontFamily: FONT }}>{card.label}</p>
            <p style={{ fontSize: "24px", fontWeight: 700, color: card.color, margin: "0 0 3px", letterSpacing: "-0.5px", fontFamily: FONT }}>{card.value}</p>
            <p style={{ fontSize: "14px", color: "#9CA3AF", margin: 0, fontFamily: FONT }}>{card.sub}</p>
          </div>
        ))}
      </div>

      <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleUpload} />

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
        <button
          onClick={() => navigate("/upload")}
          style={{
            ...btnBase,
            background: "#2563EB",
            color: "white",
            border: "none",
            fontWeight: 600,
            boxShadow: "0 1px 4px rgba(37,99,235,0.3)"
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Upload
        </button>
        <button onClick={() => setModal("newFolder")} style={btnBase}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round" /><line x1="12" y1="11" x2="12" y2="17" strokeLinecap="round" /><line x1="9" y1="14" x2="15" y2="14" strokeLinecap="round" /></svg>
          New Folder
        </button>
        <button onClick={() => setModal("share")} style={btnBase}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
          Share
        </button>
        <button onClick={() => setModal("more")} style={btnBase}>+ More</button>
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" /></svg>
          </span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search files and folders..."
            style={{ padding: "7px 12px 7px 32px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px", color: "#374151", background: "#fff", outline: "none", width: "220px", fontFamily: FONT }} />
        </div>
        <div style={{ display: "flex", border: "1px solid #E2E8F0", borderRadius: "8px", overflow: "hidden" }}>
          {[
            { mode: "list", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg> },
            { mode: "grid", icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg> },
          ].map(({ mode, icon }) => (
            <button key={mode} onClick={() => setViewMode(mode)} style={{
              background: viewMode === mode ? "#F3F4F6" : "#fff", border: "none",
              padding: "7px 10px", cursor: "pointer",
              color: viewMode === mode ? "#2563EB" : "#9CA3AF",
              display: "flex", alignItems: "center",
            }}>{icon}</button>
          ))}
        </div>
      </div>

      {/* ── Empty State ── */}
      {files.length === 0 && (
        <div style={{
          background: "#FFFFFF", border: "2px dashed #E2E8F0", borderRadius: "16px",
          padding: "64px 32px", textAlign: "center", marginBottom: "16px",
        }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📂</div>
          <p style={{ fontSize: "16px", fontWeight: 600, color: "#374151", margin: "0 0 6px", fontFamily: FONT }}>No files yet</p>
          <p style={{ fontSize: "14px", color: "#9CA3AF", margin: "0 0 20px", fontFamily: FONT }}>Upload files or create a folder to get started</p>
          <button
            onClick={() => navigate("/upload")}
            style={{
              background: "#2563EB", color: "#fff", border: "none",
              padding: "10px 20px", borderRadius: "8px", cursor: "pointer",
              fontWeight: 600, fontSize: "14px", fontFamily: FONT,
            }}
          >
            Upload your first file
          </button>
        </div>
      )}

      {/* ── List View ── */}
      {files.length > 0 && viewMode === "list" && (
        <div style={{
          background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px",
          overflow: "hidden", marginBottom: "16px",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 80px 200px 80px 60px 120px", padding: "10px 16px", borderBottom: "1px solid #F3F4F6", background: "#FAFAFA" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input type="checkbox" checked={paginated.length > 0 && paginated.every(f => selected.includes(f.id))} onChange={toggleAll} style={{ cursor: "pointer", accentColor: "#2563EB" }} />
            </div>
            {["Name", "Owner", "CID (PPS)", "Size", "Version", "Modified"].map(h => (
              <div key={h} style={{ fontSize: "11px", fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: FONT }}>{h}</div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: "14px", fontFamily: FONT }}>No results for "{search}"</div>
          )}
          {paginated.map((file, idx) => (
            <div key={file.id}
              onClick={() => navigate("/file-details", { state: { file } })}
              style={{
                display: "grid", gridTemplateColumns: "36px 1fr 80px 200px 80px 60px 120px",
                padding: "11px 16px",
                borderBottom: idx < paginated.length - 1 ? "1px solid #F9FAFB" : "none",
                background: selected.includes(file.id) ? "#EFF6FF" : "#FFFFFF",
                cursor: "pointer", transition: "background 0.12s",
              }}
              onMouseEnter={e => { if (!selected.includes(file.id)) e.currentTarget.style.background = "#F9FAFB"; }}
              onMouseLeave={e => { if (!selected.includes(file.id)) e.currentTarget.style.background = "#FFFFFF"; }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <input type="checkbox" checked={selected.includes(file.id)} onChange={() => toggleSelect(file.id)} onClick={e => e.stopPropagation()} style={{ cursor: "pointer", accentColor: "#2563EB" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <FileIcon type={file.type} />
                <span style={{ fontSize: "14px", color: "#111827", fontWeight: file.type === "folder" ? 500 : 400, fontFamily: FONT }}>{file.name}{file.source === "signed" && <SignedBadge />}<VerifyBadge status={file.verification_status} /></span>
              </div>
              <div style={{ fontSize: "14px", color: "#6B7280", display: "flex", alignItems: "center", fontFamily: FONT }}>{file.owner}</div>
              <div style={{ fontSize: "12px", color: "#6B7280", display: "flex", alignItems: "center", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.cid}</div>
              <div style={{ fontSize: "14px", color: "#6B7280", display: "flex", alignItems: "center", fontFamily: FONT }}>{file.size}</div>
              <div style={{ fontSize: "14px", color: "#6B7280", display: "flex", alignItems: "center", fontFamily: FONT }}>{file.version}</div>
              <div style={{ fontSize: "14px", color: "#6B7280", display: "flex", alignItems: "center", fontFamily: FONT }}>{file.modified}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Grid View ── */}
      {files.length > 0 && viewMode === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px,1fr))", gap: "14px", marginBottom: "16px" }}>
          {filtered.length === 0 && (
            <div style={{ gridColumn: "1/-1", padding: "40px", textAlign: "center", color: "#9CA3AF", fontSize: "14px", fontFamily: FONT }}>No results for "{search}"</div>
          )}
          {paginated.map(file => (
            <div key={file.id}
              onClick={() => navigate("/file-details", { state: { file } })}
              style={{
                background: selected.includes(file.id) ? "#EFF6FF" : "#FFFFFF",
                border: "1px solid " + (selected.includes(file.id) ? "#93C5FD" : "#E2E8F0"),
                borderRadius: "16px", padding: "24px 14px", cursor: "pointer",
                transition: "all 0.12s",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
              }}
              onMouseEnter={e => { if (!selected.includes(file.id)) { e.currentTarget.style.background = "#F9FAFB"; } }}
              onMouseLeave={e => { if (!selected.includes(file.id)) { e.currentTarget.style.background = "#FFFFFF"; } }}
            >
              <FileIcon type={file.type} />
              <span style={{ fontSize: "14px", color: "#111827", fontWeight: 500, textAlign: "center", wordBreak: "break-word", lineHeight: 1.4, fontFamily: FONT }}>{file.name}{file.source === "signed" && <SignedBadge />}<VerifyBadge status={file.verification_status} /></span>
              <span style={{ fontSize: "12px", color: "#9CA3AF", fontFamily: FONT }}>{file.modified}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {files.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: "28px" }}>
          <span style={{ fontSize: "14px", color: "#6B7280", fontFamily: FONT }}>
            Showing {filtered.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} items
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{
              border: "1px solid #E2E8F0", background: "#FFFFFF", borderRadius: "6px",
              padding: "5px 9px", cursor: currentPage === 1 ? "not-allowed" : "pointer",
              color: currentPage === 1 ? "#D1D5DB" : "#6B7280", display: "flex", alignItems: "center",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <span style={{
              background: selected.length > 0 ? "#2563EB" : "#E5E7EB",
              color: selected.length > 0 ? "#fff" : "#9CA3AF",
              borderRadius: "6px", padding: "5px 11px",
              fontSize: "14px", fontWeight: 600, minWidth: "32px", textAlign: "center", fontFamily: FONT,
            }}>{selected.length}</span>
            <button onClick={() => navigate("/upload")} style={{
              border: "1px solid #E2E8F0", background: "#FFFFFF", borderRadius: "6px",
              padding: "5px 9px", cursor: "pointer",
              color: "#6B7280", display: "flex", alignItems: "center",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}